import { createSlice } from "@reduxjs/toolkit";

import { fetchStatus } from "./preferenceSlice";
import { onDeleteExistingMapping, onSaveEditedMappings } from "./mappingSlice";

const initialState = {
  preferenceForProduct: "Do not generate invoice",
  productPreference: undefined,
  msbcFieldPreference: undefined,
  preferenceForSearchParameters: "Select search parameters",
  stripeProductSearchParameter: undefined,
  msbcProductSearchParameter: undefined,
  isInvoiceSyncRulesSaved: false,
  stripeProductMetadataPreference: "",
  invoiceTriggerFields: [
    {
      HRF: "Invoice Finalized",
      CRF: "invoice.finalized",
    },
    {
      HRF: "Invoice Paid",
      CRF: "invoice.paid",
    },
    {
      HRF: "Invoice Sent",
      CRF: "invoice.sent",
    },
  ],
  invoiceTriggerPreference: undefined,
  dealStageFields: [
    {
      HRF: "Draft",
      CRF: "draft",
    },
    {
      HRF: "Open",
      CRF: "open",
    },
    {
      HRF: "Paid",
      CRF: "paid",
    },
  ],
  dealStagePreference: undefined,
};

export const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    /**
     * Reducer updates the state for the preference of products
     */
    onChangePreferenceForProduct(state, { payload }) {
      state.preferenceForProduct = payload.preference;
      state.isInvoiceSyncRulesSaved = false;
    },
    /**
     * Reducer updates the state when user selects a product
     */
    setProductPreference(state, { payload }) {
      if (payload.selectingFor === "invoiceProduct") {
        state.productPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "msbcField") {
        state.msbcFieldPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "stripeProductSearchParameter") {
        state.stripeProductSearchParameter = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "msbcProductSearchParameter") {
        state.msbcProductSearchParameter = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "invoiceTrigger") {
        state.invoiceTriggerPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      } else if (payload.selectingFor === "dealStage") {
        state.dealStagePreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
      }
      state.isInvoiceSyncRulesSaved = false;
    },
    /**
     * Reducer updates the state for the preference for search parameters
     */
    onChangePreferenceForSearchParameters(state, { payload }) {
      state.preferenceForSearchParameters = payload.preference;
      state.isInvoiceSyncRulesSaved = false;
    },
    /**
     * Reducer updates the state of isInvoiceSyncRulesSaved
     */
    setIsInvoiceSyncRulesSaved(state, { payload }) {
      state.isInvoiceSyncRulesSaved = true;
    },
    /**
     * Reducer updates the state of productStripeMetadataPreference when user enters data in the input field
     */
    onChangeStripeProductMetadataPreference(state, { payload }) {
      state.stripeProductMetadataPreference = payload.enteredInput;
      state.isInvoiceSyncRulesSaved = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStatus.fulfilled, (state, actions) => {
      const { preference, mapping } = actions.payload;
      // Update preferences
      if (preference !== undefined && Object.keys(preference).length > 0) {
        const { invoice, product } = preference;

        if (product && Object.keys(product).length > 0) {
          // Update invoice sync rules preferences
          if (
            product.searchParameters === true ||
            product.searchParameters === false
          ) {
            state.preferenceForSearchParameters =
              product.searchParameters === false
                ? "Select search parameters"
                : "Set custom mappings";
          }

          if (product?.filterOptions) {
            if (
              product.filterOptions.filterStripeProperty.name === "MetaData"
            ) {
              state.stripeProductSearchParameter = {
                name: "MetaData",
                value: "key",
              };
              state.stripeProductMetadataPreference =
                product.filterOptions.filterStripeProperty.value;
            } else {
              state.stripeProductSearchParameter = product.filterOptions
                ?.filterStripeProperty?.value
                ? product.filterOptions.filterStripeProperty
                : undefined;
            }
            state.msbcProductSearchParameter = product.filterOptions
              ?.filterMsbcProperty?.value
              ? product.filterOptions.filterMsbcProperty
              : undefined;
          }

          if (product?.generateInvoice) {
            state.preferenceForProduct =
              product.generateInvoice?.create === true
                ? "Use default product"
                : "Do not generate invoice";

            state.productPreference = product.generateInvoice?.mapping
              ?.msbcDefaultProduct?.value
              ? product.generateInvoice.mapping.msbcDefaultProduct
              : undefined;
          }
        }

        if (invoice && Object.keys(invoice).length > 0) {
          if (invoice?.stripeInvoiceId) {
            if (Object.keys(invoice.stripeInvoiceId.mapping).length > 0) {
              state.msbcFieldPreference = invoice.stripeInvoiceId?.mapping
                ?.value
                ? invoice.stripeInvoiceId.mapping
                : undefined;
            }
          }

          if (invoice?.triggerSettings) {
            if (Object.keys(invoice.triggerSettings.invoiceStage)) {
              state.invoiceTriggerPreference = invoice.triggerSettings
                ?.invoiceStage?.value
                ? invoice.triggerSettings.invoiceStage
                : undefined;
            }
          }

          if (invoice?.actionSettings) {
            if (Object.keys(invoice.actionSettings.dealStage)) {
              state.dealStagePreference = invoice.actionSettings?.dealStage
                ?.value
                ? invoice.actionSettings.dealStage
                : undefined;
            }
          }
        }
      }

      if (
        ((state.preferenceForProduct === "Use default product" &&
          state.productPreference) ||
          state.preferenceForProduct === "Do not generate invoice") &&
        ((state.preferenceForSearchParameters === "Select search parameters" &&
          state.msbcProductSearchParameter &&
          state.stripeProductSearchParameter) ||
          (state.preferenceForSearchParameters === "Set custom mappings" &&
            mapping.product.length > 0)) &&
        state.invoiceTriggerPreference
      ) {
        state.isInvoiceSyncRulesSaved = true;
      }
    });
    builder.addCase(onDeleteExistingMapping, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "product"
      ) {
        state.isInvoiceSyncRulesSaved = false;
      }
    });
    builder.addCase(onSaveEditedMappings, (state, actions) => {
      if (
        actions.payload.mappingsFor &&
        actions.payload.mappingsFor === "product"
      ) {
        state.isInvoiceSyncRulesSaved = false;
      }
    });
  },
});

export const {
  onChangePreferenceForProduct,
  setProductPreference,
  onChangePreferenceForSearchParameters,
  setIsInvoiceSyncRulesSaved,
  onChangeStripeProductMetadataPreference,
} = invoiceSlice.actions;

export default invoiceSlice.reducer;
